import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Avatar,
  Button,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Person, Business, LocationOn, Lock } from '@mui/icons-material';
import ChildCard from '@/components/shared/ChildCard';
import { apiService } from '@/services/api';
import { useTranslation } from '@/hooks/useTranslation';
import { useAuth } from '@/context/AuthContext.tsx';

interface Country {
  id: string;
  name: string;
  isoCode: string;
  phoneCode: string;
}

const ProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const { user } = useAuth();

  const [personalInfo, setPersonalInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
  });

  const [companyInfo, setCompanyInfo] = useState({
    companyName: '',
    vatNumber: '',
    evcNumber: '',
    autotunerEmail: '',
    magicmotorsportsEmail: '',
  });

  const [addressInfo, setAddressInfo] = useState({
    countryId: '',
    city: '',
    address: '',
    postCode: '',
  });

  const [passwordInfo, setPasswordInfo] = useState({
    oldPassword: '',
    newPassword: '',
  });

  const [countries, setCountries] = useState<Country[]>([]);
  const [loadingCountries, setLoadingCountries] = useState(false);

  // Load countries
  useEffect(() => {
    const loadCountries = async () => {
      setLoadingCountries(true);
      try {
        const response = await apiService.getCountries();
        setCountries(response.items);
      } catch (error) {
        console.error('Error loading countries:', error);
      } finally {
        setLoadingCountries(false);
      }
    };

    loadCountries();
  }, []);

  // Load profile data
  useEffect(() => {
    const loadProfile = async () => {
      setLoading(true);
      setError(null);
      try {
        const profile = await apiService.getProfile();
        
        setPersonalInfo({
          firstName: profile.firstName || '',
          lastName: profile.lastName || '',
          email: profile.email || '',
          phoneNumber: profile.phoneNumber || '',
        });

        if (profile.customer) {
          setCompanyInfo({
            companyName: profile.customer.companyName || '',
            vatNumber: profile.customer.vatNumber || '',
            evcNumber: profile.customer.evcNumber || '',
            autotunerEmail: profile.customer.autotunerEmail || '',
            magicmotorsportsEmail: profile.customer.magicmotorsportsEmail || '',
          });

          setAddressInfo({
            countryId: profile.customer.countryId || '',
            city: profile.customer.city || '',
            address: profile.customer.address || '',
            postCode: profile.customer.postCode || '',
          });
        }
      } catch (error: any) {
        console.error('Error loading profile:', error);
        setError(error.message || t('profile.error'));
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, []);

  const handlePersonalInfoChange =
    (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setPersonalInfo(prev => ({
        ...prev,
        [field]: event.target.value,
      }));
      setSuccess(false);
    };

  const handleCompanyInfoChange =
    (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setCompanyInfo(prev => ({
        ...prev,
        [field]: event.target.value,
      }));
      setSuccess(false);
    };

  const handleAddressInfoChange =
    (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setAddressInfo(prev => ({
        ...prev,
        [field]: event.target.value,
      }));
      setSuccess(false);
    };

  const handleCountryChange = (event: any) => {
    setAddressInfo(prev => ({
      ...prev,
      countryId: event.target.value,
    }));
    setSuccess(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const profileData: {
        firstName: string;
        lastName: string;
        phoneNumber: string;
        companyName: string;
        vatNumber: string;
        evcNumber: string;
        city: string;
        address: string;
        postCode: string;
        countryId: string;
        autotunerEmail?: string;
        magicmotorsportsEmail?: string;
      } = {
        firstName: personalInfo.firstName,
        lastName: personalInfo.lastName,
        phoneNumber: personalInfo.phoneNumber,
        companyName: companyInfo.companyName,
        vatNumber: companyInfo.vatNumber,
        evcNumber: companyInfo.evcNumber,
        city: addressInfo.city,
        address: addressInfo.address,
        postCode: addressInfo.postCode,
        countryId: addressInfo.countryId,
      };

      // Додаємо email поля тільки якщо вони не пусті
      if (companyInfo.autotunerEmail && companyInfo.autotunerEmail.trim() !== '') {
        profileData.autotunerEmail = companyInfo.autotunerEmail.trim();
      }
      if (companyInfo.magicmotorsportsEmail && companyInfo.magicmotorsportsEmail.trim() !== '') {
        profileData.magicmotorsportsEmail = companyInfo.magicmotorsportsEmail.trim();
      }

      await apiService.updateProfile(profileData);

      setSuccess(true);
    } catch (error: any) {
      console.error('Error saving profile:', error);
      setError(error.message || t('profile.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordInfo(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
    setError(null);
  };

  const handleChangePassword = async () => {
    setChangingPassword(true);
    setError(null);
    setSuccess(false);

    try {
      await apiService.changePassword({
        oldPassword: passwordInfo.oldPassword,
        newPassword: passwordInfo.newPassword,
      });

      setSuccess(true);
      setPasswordInfo({ oldPassword: '', newPassword: '' });
      // Return to profile view after successful password change
      setTimeout(() => {
        setShowChangePassword(false);
        setSuccess(false);
      }, 2000);
    } catch (error: any) {
      console.error('Error changing password:', error);
      setError(error.message || t('profile.changePassword.error'));
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {showChangePassword ? t('profile.changePassword.title') : t('profile.title')}
      </Typography>

      {error && (
        <Alert variant="filled" severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="filled" severity="success" sx={{ mb: 3, color: 'white' }} onClose={() => setSuccess(false)}>
          {showChangePassword ? t('profile.changePassword.success') : t('profile.success')}
        </Alert>
      )}

      <ChildCard>
        {showChangePassword ? (
          <>
            {/* Change Password Form */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Lock sx={{ color: 'primary.main', mr: 1 }} />
                <Typography variant="h6" color="primary">
                  {t('profile.changePassword.title')}
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label={t('profile.changePassword.oldPassword')}
                    type="password"
                    value={passwordInfo.oldPassword}
                    onChange={handlePasswordChange('oldPassword')}
                    variant="outlined"
                    fullWidth
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label={t('profile.changePassword.newPassword')}
                    type="password"
                    value={passwordInfo.newPassword}
                    onChange={handlePasswordChange('newPassword')}
                    variant="outlined"
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
              <Button
                variant="outlined"
                color="primary"
                size="large"
                onClick={() => {
                  setShowChangePassword(false);
                  setPasswordInfo({ oldPassword: '', newPassword: '' });
                  setError(null);
                }}
                disabled={changingPassword}
              >
                {t('profile.changePassword.cancel')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleChangePassword}
                disabled={changingPassword || !passwordInfo.oldPassword || !passwordInfo.newPassword}
                sx={{ minWidth: '120px' }}
              >
                {changingPassword ? <CircularProgress size={24} /> : t('profile.changePassword.changeButton')}
              </Button>
            </Box>
          </>
        ) : (
          <>
        {/* Avatar Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <Avatar
            sx={{
              width: 80,
              height: 80,
              mr: 3,
              bgcolor: 'primary.main',
              fontSize: '2rem',
              fontWeight: 'bold',
            }}
          >
            {(() => {
              if (user?.customer?.customerType?.name === 'Business' && companyInfo.companyName) {
                return companyInfo.companyName.charAt(0).toUpperCase();
              }
              return personalInfo.firstName.charAt(0).toUpperCase() || 'U';
            })()}
          </Avatar>
          <Box>
            <Typography variant="h5" gutterBottom>
              {(() => {
                if (user?.customer?.customerType?.name === 'Business' && companyInfo.companyName) {
                  return companyInfo.companyName;
                }
                return `${personalInfo.firstName} ${personalInfo.lastName}`.trim();
              })()}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {personalInfo.email}
            </Typography>
          </Box>
        </Box>

        {/* Personal Information */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Person sx={{ color: 'primary.main', mr: 1 }} />
            <Typography variant="h6" color="primary">
              {t('profile.personalInfo.title')}
            </Typography>
          </Box>

          <Grid container spacing={2}>
            {user?.customer?.customerType?.name === 'Business' ? (
              <Grid size={{ xs: 12 }}>
                <TextField
                  label={t('profile.companyInfo.companyName')}
                  value={companyInfo.companyName}
                  onChange={handleCompanyInfoChange('companyName')}
                  variant="outlined"
                  fullWidth
                />
              </Grid>
            ) : (
              <>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label={t('profile.personalInfo.firstName')}
                    value={personalInfo.firstName}
                    onChange={handlePersonalInfoChange('firstName')}
                    variant="outlined"
                    fullWidth
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label={t('profile.personalInfo.lastName')}
                    value={personalInfo.lastName}
                    onChange={handlePersonalInfoChange('lastName')}
                    variant="outlined"
                    fullWidth
                  />
                </Grid>
              </>
            )}
            <Grid size={{ xs: 12 }}>
              <TextField
                label={t('profile.personalInfo.email')}
                value={personalInfo.email}
                variant="outlined"
                fullWidth
                disabled
                helperText={t('profile.personalInfo.emailHelper')}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label={t('profile.personalInfo.phoneNumber')}
                value={personalInfo.phoneNumber}
                onChange={handlePersonalInfoChange('phoneNumber')}
                variant="outlined"
                fullWidth
                placeholder={t('profile.personalInfo.phonePlaceholder')}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Company Information */}
            {user?.userType === 'customer' && (
              <>
                <Divider sx={{ my: 3 }} />
                <Box sx={{ mb: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Business sx={{ color: 'primary.main', mr: 1 }} />
                    <Typography variant="h6" color="primary">
                      {t('profile.companyInfo.title')}
                    </Typography>
                  </Box>

                  <Grid container spacing={2}>
                    {user?.customer?.customerType?.name === 'Business' && (
                      <Grid size={{ xs: 12, sm: 12 }}>
                        <TextField
                          label={t('profile.companyInfo.vatNumber')}
                          value={companyInfo.vatNumber}
                          onChange={handleCompanyInfoChange('vatNumber')}
                          variant="outlined"
                          fullWidth
                        />
                      </Grid>
                    )}
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        label={t('profile.companyInfo.evcNumber')}
                        value={companyInfo.evcNumber}
                        onChange={handleCompanyInfoChange('evcNumber')}
                        variant="outlined"
                        fullWidth
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label={t('profile.companyInfo.autotunerEmail')}
                        type="email"
                        value={companyInfo.autotunerEmail}
                        onChange={handleCompanyInfoChange('autotunerEmail')}
                        variant="outlined"
                        fullWidth
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label={t('profile.companyInfo.magicmotorsportsEmail')}
                        type="email"
                        value={companyInfo.magicmotorsportsEmail}
                        onChange={handleCompanyInfoChange('magicmotorsportsEmail')}
                        variant="outlined"
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Address Information */}
                <Box sx={{ mb: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <LocationOn sx={{ color: 'primary.main', mr: 1 }} />
                    <Typography variant="h6" color="primary">
                      {t('profile.addressInfo.title')}
                    </Typography>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormControl fullWidth>
                        <InputLabel>{t('profile.addressInfo.country')}</InputLabel>
                        <Select
                          value={addressInfo.countryId}
                          onChange={handleCountryChange}
                          label={t('profile.addressInfo.country')}
                          disabled={loadingCountries}
                        >
                          {loadingCountries ? (
                            <MenuItem disabled>
                              <CircularProgress size={20} />
                            </MenuItem>
                          ) : (
                            countries.map(country => (
                              <MenuItem key={country.id} value={country.id}>
                                {country.name}
                              </MenuItem>
                            ))
                          )}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label={t('profile.addressInfo.city')}
                        value={addressInfo.city}
                        onChange={handleAddressInfoChange('city')}
                        variant="outlined"
                        fullWidth
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label={t('profile.addressInfo.address')}
                        value={addressInfo.address}
                        onChange={handleAddressInfoChange('address')}
                        variant="outlined"
                        fullWidth
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label={t('profile.addressInfo.postCode')}
                        value={addressInfo.postCode}
                        onChange={handleAddressInfoChange('postCode')}
                        variant="outlined"
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </Box>
              </>
            )}

        {/* Save Button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
          <Button
            variant="outlined"
            color="primary"
            size="large"
            onClick={() => setShowChangePassword(true)}
            sx={{ minWidth: '150px' }}
          >
            {t('profile.changePasswordButton')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleSave}
            disabled={saving}
            sx={{ minWidth: '120px' }}
          >
            {saving ? <CircularProgress size={24} /> : t('profile.save')}
          </Button>
        </Box>
          </>
        )}
      </ChildCard>
    </Box>
  );
};

export default ProfilePage;
